"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useMemo, useCallback, memo, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";
import { Drawer } from "vaul";
import { classNames } from "@/lib/utils";
import { useGetAllAccountsForUser } from "@/hooks/queries/accounts";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { useAccountSwitcher } from "@/contexts/AccountSwitcherContext";
import { useLocalizedAppPath } from "@/contexts/AppLocaleContext";
import { UserAvatar, preloadAvatar } from "@/components/ui/user-avatar";
import { getStoredSelectedAccountId } from "./SidebarAccountSelector";
import { proxyImageUrl } from "@/lib/image-proxy";
import { PlusIcon, Cog6ToothIcon, ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/outline";
import { AccountByIdData } from "@/hooks/queries/accounts";
import { TrendingUpIcon } from "@/components/ui/trending-up";
import { SparklesIcon } from "@/components/ui/sparkles-icon";
import { RocketIcon } from "@/components/ui/rocket-icon";

// ============================================
// TYPES
// ============================================
type AccountWithPlatform = AccountByIdData & {
  platform?: "instagram" | "tiktok";
  account_type?: "trial" | "go" | "grow" | "glory" | "archived";
};

// ============================================
// UTILITIES
// ============================================
function formatFollowers(count: number | null | undefined): string {
  if (!count) return "0";
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return `${count}`;
}

// ============================================
// PLAN BADGE - Matches desktop compact size exactly
// ============================================
function PlanBadge(t0) {
  const $ = _c(4);
  const {
    accountType
  } = t0;
  if (accountType === "trial") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className={classNames("inline-flex items-center justify-center gap-1.5 rounded-none h-[20px] px-[7px] text-[10px] font-extrabold uppercase tracking-wider", "bg-amber-100 text-amber-700")}><span className="translate-y-[0.5px]">Trial</span></span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (accountType === "basic" || accountType === "go") {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className={classNames("inline-flex items-center justify-center gap-1.5 rounded-none h-[20px] px-[7px] text-[10px] font-extrabold uppercase tracking-wider", "bg-[#3b82f6] text-white")}><TrendingUpIcon size={12} /><span className="translate-y-[0.5px]">Go</span></span>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (accountType === "pro" || accountType === "grow") {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className={classNames("inline-flex items-center justify-center gap-1.5 rounded-none h-[20px] px-[7px] text-[10px] font-extrabold uppercase tracking-wider", "bg-[#ec4899] text-white")}><SparklesIcon size={12} /><span className="translate-y-[0.5px]">Grow</span></span>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (accountType === "turbo" || accountType === "glory") {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className={classNames("inline-flex items-center justify-center gap-1.5 rounded-none h-[20px] px-[7px] text-[10px] font-extrabold uppercase tracking-wider", "bg-[#059669] text-white")}><RocketIcon size={12} /><span className="translate-y-[0.5px]">Glory</span></span>;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  return null;
}

// ============================================
// PLATFORM ICON
// ============================================
function PlatformIcon(t0) {
  const $ = _c(4);
  const {
    platform,
    size: t1
  } = t0;
  const size = t1 === undefined ? 12 : t1;
  if (platform === "instagram") {
    let t2;
    if ($[0] !== size) {
      t2 = <Image src="/img/icons/instagram.svg" width={size} height={size} alt="" className="rounded-[2px]" />;
      $[0] = size;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (platform === "tiktok") {
    let t2;
    if ($[2] !== size) {
      t2 = <Image src="/img/icons/tiktok.svg" width={size} height={size} alt="" />;
      $[2] = size;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  return null;
}

// ============================================
// ACCOUNT ROW (for drawer)
// ============================================
interface AccountRowProps {
  account: AccountWithPlatform;
  isSelected?: boolean;
  onClick?: () => void;
}
const AccountRow = memo(function AccountRow(t0) {
  const $ = _c(34);
  const {
    account,
    isSelected,
    onClick
  } = t0;
  const t1 = isSelected ? "bg-gray-100" : "active:bg-gray-50";
  let t2;
  if ($[0] !== t1) {
    t2 = classNames("group flex w-full items-center gap-3 rounded-xl px-3 py-3 transition-all duration-150", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== account.avatar_url) {
    t3 = proxyImageUrl(account.avatar_url);
    $[2] = account.avatar_url;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== account.username || $[5] !== t3) {
    t4 = <UserAvatar src={t3} username={account.username} size={44} rounded="xl" ring={false} />;
    $[4] = account.username;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== account.platform) {
    t5 = <div className="absolute -bottom-0.5 -right-0.5"><PlatformIcon platform={account.platform} size={16} /></div>;
    $[7] = account.platform;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="relative shrink-0">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== account.username) {
    t7 = <span className="truncate text-base font-semibold text-gray-900">{account.username}</span>;
    $[12] = account.username;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== account.account_type) {
    t8 = <PlanBadge accountType={account.account_type} />;
    $[14] = account.account_type;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== account.followers) {
    t9 = formatFollowers(account.followers);
    $[16] = account.followers;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <p className="text-sm text-gray-500"><span className="font-medium text-gray-600">{t9}</span> followers</p>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t8) {
    t11 = <div className="mt-0.5 flex items-center gap-2">{t8}{t10}</div>;
    $[20] = t10;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t7) {
    t12 = <div className="min-w-0 flex-1 text-left">{t7}{t11}</div>;
    $[23] = t11;
    $[24] = t7;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== isSelected) {
    t13 = isSelected && <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-900"><svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div>;
    $[26] = isSelected;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== onClick || $[29] !== t12 || $[30] !== t13 || $[31] !== t2 || $[32] !== t6) {
    t14 = <button onClick={onClick} className={t2}>{t6}{t12}{t13}</button>;
    $[28] = onClick;
    $[29] = t12;
    $[30] = t13;
    $[31] = t2;
    $[32] = t6;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
});

// ============================================
// MAIN COMPONENT
// ============================================
/**
 * MobileAccountIndicator - Compact account avatar for mobile header
 * Tapping opens a drawer to switch accounts
 * Only visible on mobile (md:hidden)
 */
const MobileAccountIndicator = memo(function MobileAccountIndicator() {
  const $ = _c(98);
  const router = useRouter();
  const pathname = usePathname();
  const {
    isOpen,
    open,
    close
  } = useAccountSwitcher();
  const {
    setIsConnectAccountModalOpened
  } = useModalContext();
  const localizedPath = useLocalizedAppPath();
  const {
    data: accountsData,
    isLoading
  } = useGetAllAccountsForUser();
  const accounts = accountsData as AccountWithPlatform[] | null ?? null;
  const [storedAccountId, setStoredAccountId] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setStoredAccountId(getStoredSelectedAccountId());
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let urlId;
  if ($[2] !== pathname) {
    urlId = pathname.split("/")[3];
    t2 = urlId?.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/);
    $[2] = pathname;
    $[3] = t2;
    $[4] = urlId;
  } else {
    t2 = $[3];
    urlId = $[4];
  }
  const isValidUUID = t2;
  const currentAccountId = isValidUUID ? urlId : storedAccountId;
  let t3;
  bb0: {
    if (!accounts || accounts.length === 0) {
      t3 = null;
      break bb0;
    }
    let t4;
    if ($[5] !== accounts || $[6] !== currentAccountId) {
      t4 = accounts.find(a => a.id === currentAccountId) || accounts[0];
      $[5] = accounts;
      $[6] = currentAccountId;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t3 = t4;
  }
  const currentAccount = t3;
  let t4;
  let t5;
  if ($[8] !== accounts) {
    t4 = () => {
      if (accounts) {
        accounts.forEach(_temp);
      }
    };
    t5 = [accounts];
    $[8] = accounts;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== close || $[12] !== router) {
    t6 = account_0 => {
      close();
      router.push(`/app/account/${account_0.id}`);
    };
    $[11] = close;
    $[12] = router;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleSelectAccount = t6;
  let t7;
  if ($[14] !== close || $[15] !== setIsConnectAccountModalOpened) {
    t7 = () => {
      close();
      setIsConnectAccountModalOpened(true);
    };
    $[14] = close;
    $[15] = setIsConnectAccountModalOpened;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleConnectAccount = t7;
  const switchToScrollRef = useRef(null);
  const [canScrollTop, setCanScrollTop] = useState(false);
  const [canScrollBottom, setCanScrollBottom] = useState(false);
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const el = switchToScrollRef.current;
      if (!el) {
        return;
      }
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = el;
      setCanScrollTop(scrollTop > 0);
      setCanScrollBottom(scrollTop + clientHeight < scrollHeight - 5);
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const checkScroll = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      checkScroll();
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== accounts) {
    t10 = [accounts, checkScroll];
    $[19] = accounts;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  useEffect(t9, t10);
  if (isLoading || !currentAccount) {
    let t11;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="flex md:hidden"><div className="h-8 w-8 animate-pulse rounded-lg bg-gray-100" /></div>;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  let T0;
  let T1;
  let T2;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[22] !== accounts || $[23] !== canScrollBottom || $[24] !== canScrollTop || $[25] !== close || $[26] !== currentAccount || $[27] !== handleSelectAccount || $[28] !== isOpen || $[29] !== open) {
    const otherAccounts = accounts?.filter(a_0 => a_0.id !== currentAccount.id) || [];
    t20 = "flex md:hidden";
    let t22;
    if ($[44] !== currentAccount.avatar_url) {
      t22 = proxyImageUrl(currentAccount.avatar_url);
      $[44] = currentAccount.avatar_url;
      $[45] = t22;
    } else {
      t22 = $[45];
    }
    let t23;
    if ($[46] !== currentAccount.username || $[47] !== t22) {
      t23 = <UserAvatar src={t22} username={currentAccount.username} size={32} rounded="lg" ring={false} />;
      $[46] = currentAccount.username;
      $[47] = t22;
      $[48] = t23;
    } else {
      t23 = $[48];
    }
    let t24;
    if ($[49] !== currentAccount.platform) {
      t24 = <div className="absolute -bottom-0.5 -right-0.5"><PlatformIcon platform={currentAccount.platform} size={12} /></div>;
      $[49] = currentAccount.platform;
      $[50] = t24;
    } else {
      t24 = $[50];
    }
    if ($[51] !== open || $[52] !== t23 || $[53] !== t24) {
      t21 = <button type="button" onClick={open} className="relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors active:bg-gray-100" aria-label="Switch account">{t23}{t24}</button>;
      $[51] = open;
      $[52] = t23;
      $[53] = t24;
      $[54] = t21;
    } else {
      t21 = $[54];
    }
    T2 = Drawer.Root;
    t18 = isOpen;
    if ($[55] !== close) {
      t19 = open_0 => !open_0 && close();
      $[55] = close;
      $[56] = t19;
    } else {
      t19 = $[56];
    }
    T1 = Drawer.Portal;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    T0 = Drawer.Content;
    t11 = "fixed bottom-0 left-0 right-0 z-50 flex max-h-[85vh] flex-col rounded-t-3xl bg-white outline-none";
    t12 = undefined;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Drawer.Title className="sr-only">Switch Account</Drawer.Title>;
      $[58] = t13;
    } else {
      t13 = $[58];
    }
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div className="flex justify-center pb-2 pt-4"><div className="h-1.5 w-12 rounded-full bg-gray-300" /></div>;
      $[59] = t14;
    } else {
      t14 = $[59];
    }
    let t25;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <p className="mb-2 px-3 text-[13px] font-semibold uppercase tracking-wider text-gray-400">Current Account</p>;
      $[60] = t25;
    } else {
      t25 = $[60];
    }
    if ($[61] !== currentAccount) {
      t15 = <div className="px-4 pb-2">{t25}<AccountRow account={currentAccount} isSelected={true} /></div>;
      $[61] = currentAccount;
      $[62] = t15;
    } else {
      t15 = $[62];
    }
    t16 = otherAccounts.length > 0 && <div className="border-t border-gray-100 px-4 py-3"><p className="mb-2 px-3 text-[13px] font-semibold uppercase tracking-wider text-gray-400">Switch to</p><div className="relative"><div className={classNames("pointer-events-none absolute left-0 right-0 top-0 z-10 h-6 bg-gradient-to-b from-white to-transparent transition-opacity duration-200", canScrollTop ? "opacity-100" : "opacity-0")} /><div ref={switchToScrollRef} onScroll={checkScroll} className="scrollbar-hide max-h-[240px] overflow-y-auto">{otherAccounts.map(account_1 => <AccountRow key={account_1.id} account={account_1} onClick={() => handleSelectAccount(account_1)} />)}</div><div className={classNames("pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-6 bg-gradient-to-t from-white to-transparent transition-opacity duration-200", canScrollBottom ? "opacity-100" : "opacity-0")} /></div></div>;
    $[22] = accounts;
    $[23] = canScrollBottom;
    $[24] = canScrollTop;
    $[25] = close;
    $[26] = currentAccount;
    $[27] = handleSelectAccount;
    $[28] = isOpen;
    $[29] = open;
    $[30] = T0;
    $[31] = T1;
    $[32] = T2;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    T0 = $[30];
    T1 = $[31];
    T2 = $[32];
    t11 = $[33];
    t12 = $[34];
    t13 = $[35];
    t14 = $[36];
    t15 = $[37];
    t16 = $[38];
    t17 = $[39];
    t18 = $[40];
    t19 = $[41];
    t20 = $[42];
    t21 = $[43];
  }
  let t22;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <PlusIcon className="h-5 w-5" strokeWidth={2.5} />;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== handleConnectAccount) {
    t23 = <div className="border-t border-gray-100 px-4 pt-4"><button onClick={handleConnectAccount} className="flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-gray-900 text-base font-semibold text-white transition-all duration-200 active:scale-[0.98]">{t22}Add Account</button></div>;
    $[64] = handleConnectAccount;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== localizedPath) {
    t24 = localizedPath("/app/settings");
    $[66] = localizedPath;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Cog6ToothIcon className="h-5 w-5" />;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== close || $[70] !== t24) {
    t26 = <a href={t24} onClick={close} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-gray-100 text-base font-medium text-gray-700 transition-colors active:bg-gray-200">{t25}Settings</a>;
    $[69] = close;
    $[70] = t24;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <a href="/api/logout" className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-gray-100 text-base font-medium text-red-600 transition-colors active:bg-red-50"><ArrowRightStartOnRectangleIcon className="h-5 w-5" />Sign Out</a>;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t26) {
    t28 = <div className="pb-safe flex gap-2 px-4 pb-4 pt-3">{t26}{t27}</div>;
    $[73] = t26;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== T0 || $[76] !== t11 || $[77] !== t12 || $[78] !== t13 || $[79] !== t14 || $[80] !== t15 || $[81] !== t16 || $[82] !== t23 || $[83] !== t28) {
    t29 = <T0 className={t11} aria-describedby={t12}>{t13}{t14}{t15}{t16}{t23}{t28}</T0>;
    $[75] = T0;
    $[76] = t11;
    $[77] = t12;
    $[78] = t13;
    $[79] = t14;
    $[80] = t15;
    $[81] = t16;
    $[82] = t23;
    $[83] = t28;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== T1 || $[86] !== t17 || $[87] !== t29) {
    t30 = <T1>{t17}{t29}</T1>;
    $[85] = T1;
    $[86] = t17;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  let t31;
  if ($[89] !== T2 || $[90] !== t18 || $[91] !== t19 || $[92] !== t30) {
    t31 = <T2 open={t18} onOpenChange={t19}>{t30}</T2>;
    $[89] = T2;
    $[90] = t18;
    $[91] = t19;
    $[92] = t30;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  let t32;
  if ($[94] !== t20 || $[95] !== t21 || $[96] !== t31) {
    t32 = <div className={t20}>{t21}{t31}</div>;
    $[94] = t20;
    $[95] = t21;
    $[96] = t31;
    $[97] = t32;
  } else {
    t32 = $[97];
  }
  return t32;
});
export default MobileAccountIndicator;
function _temp(account) {
  if (account.avatar_url) {
    preloadAvatar(proxyImageUrl(account.avatar_url));
  }
}